import {gsap} from "gsap/all";
import $ from "jquery";
import "../../libs/limacharlie.helpers.js";
import {i18n} from '/js/app/base/i18n.js'
import {AudioPlayer} from './Audio.js'
import nunjucks from "nunjucks"
import seedrandom from "seedrandom";

export class BaseGame {

  constructor(dataFile = 'data/data.json') {
    this.ready = false;
    this.started = false;
    this.lockScreen = false;

    // Data and settings file
    this.dataFile = dataFile;
    this.data = {};
    this.settingsFile = 'settings.json';
    this.fonts = [
      // new FontFaceObserver("Work Sans", {weight: 300}),
      // new FontFaceObserver("Work Sans", {weight: 400}),
    ]; // to be overridden in child if needed, use FontFaceObservers

    // Hold current screen
    this.currentScreen = 0;
    this.targetScreen = null;
    this.$currentScreen = null;

    // Hold current step (within a screen)
    this.currentStep = 1;
    this.targetStep = null;

    // Hold current action (in progress)
    this.currentAction = false;

    // Touch event valid with the NWJS version <= 38
    this.isTouch = (('ontouchstart' in window) || window.navigator.maxTouchPoints > 0 || (window.DocumentTouch && document instanceof DocumentTouch)) && (window.getURLParameter("forcetouchevent") !== "click");
    this.touchEvent = this.isTouch ? "touchstart" : "click";
    this.touchEvent += " keydown"; // Add accessibility features

    // Init i18n
    this.i18n = new i18n(this);

    // Init Audio
    this.audio = new AudioPlayer(this);

    // Init buttons
    this.initButtons();

    // When opened in Electron, disable right-click
    console.log("In Electron App?", window.inElectron());
    if (window.inElectron()) {
      $("#main").addClass("in-electron")
      $("body").css("cursor", "none").on("contextmenu", function (e) {
        return false;
      });
      $(document).contextmenu(function (e) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
    } else {
      $("#main").addClass("in-browser")
      $(window).keypress(function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') {
          $("#ref").toggle();
        }
      })
    }

    // Screensaver
    this.screensaverDelay = 30;
    this.screensaverResetDelay = 30;
    this.rfidRemovedDelay = 10;
    this.lockScreenSaver = false;
    this.lastClick = 0;
    this.lastReset = 0;
  }

  // Load settings JSON (with promise)
  loadSettings() {
    console.log("Loading settings...");
    return new Promise((resolve, reject) => {
      $.ajax({
        dataType: "json",
        url: this.settingsFile,
        cache: false,
      }).done((data) => {
        this.settings = data;
        // Init screensaver
        if (typeof this.settings.screensaver_delay_sec != "undefined") {
          this.screensaverDelay = this.settings.screensaver_delay_sec;
        }
        if (typeof this.settings.screensaver_reset_sec != "undefined") {
          this.screensaverResetDelay = this.settings.screensaver_reset_sec;
        }
        if (typeof this.settings.rfid_removed_delay != "undefined") {
          this.rfidRemovedDelay = this.settings.rfid_removed_delay;
        }

        if (typeof this.settings.default_language != "undefined") {
          this.i18n.set(this.settings.default_language);
        }
        resolve();
      }).fail(() => {
        const message = "The settings.json file was not found. Please add the setting file and restart app.";
        if (window.inElectron()) {
          alert(message);
          window.electronBridge.quit()
        }
        reject(new Error(message));
      });
    });
  };

  // Load data JSON (with promise)
  loadData(filename = null) {
    console.log("Load data from...", filename ?? this.dataFile);
    return new Promise((resolve, reject) => {
      $.ajax({
        dataType: "json",
        url: filename ?? this.dataFile,
        cache: false,
      }).done((data) => {
        // console.log(data);
        this.data = this.deepMerge(this.data, data);
        // this.data = {...this.data, ...data};
        resolve();
      }).fail(() => {
        const message = `No data found for ${filename}. Please add the data file and restart the app.`;
        if (window.inElectron()) {
          alert(message);
          window.electronBridge.quit()
        }
        reject(new Error(message));
      });
    });
  };

  // The volume of settings.json, for the sounds the interactive does not give
  // a volume of their own.
  getVolume() {
    if (typeof this.settings.volume === "number") return this.settings.volume;
    return 1;
  }

  // Preload audio files
  preloadAudio() {
    console.log("Preloading audio...");
    const allAudioFiles = this.findAllAudioFiles(this.data);
    if (typeof this.settings.click_sound !== "undefined" && this.settings.click_sound != "") {
      allAudioFiles.push(this.settings.click_sound);
    }
    const uniqueAudioFiles = allAudioFiles.filter((value, index, array) => array.indexOf(value) === index);

    // Preload audio
    return this.audio.preload(uniqueAudioFiles)
  };

  preloadFonts(timeout = 30000) {
    console.log("Preloading fonts...");

    // Load each observer. The second arg to .load() is the timeout (ms); the
    // observer rejects if the font isn't ready in time. Wrap each so the
    // rejection names the font (the library's own error doesn't).
    const observers = this.fonts.map((font) =>
      font.load(null, timeout).catch((err) => {
        const name = `${font.family} ${font.weight} ${font.style}`;
        const error = new Error(`Font "${name}" failed to load within ${timeout}ms`);
        error.fontTimeout = true; // tag so the boot chain's catch can restart the app
        error.cause = err;
        throw error;
      })
    );

    // resolves immediately with an empty array when passed an empty array so will work even if this.fonts is empty
    return Promise.all(observers);
  }

  // Init nunjucks
  initNunjucks() {
    console.log("Init nunjucks...");
    return new Promise((resolve, reject) => {
      this.nunjuckEnv = nunjucks.configure('', {
        autoescape: false,
      });

      /*
      /* Nunjucks custom filters
      */
      // Split words into multiple span
      this.nunjuckEnv.addFilter("splitWords", function (text) {
        if (typeof text !== "string") return text;

        // Split text into lines (preserving \r\n / \n), wrap each word in <span>
        return text.split(/\r?\n/).map(line =>
          line.split(/\s+/).map(word => `<span>${word}</span>`).join(" ")
        ).join("<br>");
      });

      // Add a way to list object keys like an array
      this.nunjuckEnv.addFilter('keys', obj => {
        return Object.keys(obj)
      });

      // Add a way to list object keys like an array
      this.nunjuckEnv.addFilter('shuffleWithSeed', obj => {
        const shuffledKeys = Object.keys(this.data.content.team_members.members_list);
        const rng = seedrandom("laviecestuneboitedechocolatmaischialepas");
        for (let i = shuffledKeys.length - 1; i > 0; i--) {
          const j = Math.floor(rng() * (i + 1));
          [shuffledKeys[i], shuffledKeys[j]] = [shuffledKeys[j], shuffledKeys[i]];
        }
        return Object.fromEntries(
          shuffledKeys
            .filter(key => key in obj) // 1. Ensure the key actually exists
            .map(key => [key, obj[key]]) // 2. Map keys to [key, value] pairs
        );
      });

      this.mainNunjuckTemplate = nunjucks.compile($("#main").html(), this.nunjuckEnv);
      resolve();
    });
  }

  // Render twig style with nunjucks
  renderNunjucks() {
    console.log("Render nunjucks...");
    return new Promise((resolve, reject) => {
      const res = this.mainNunjuckTemplate.render({data: this.data, visitorData: null, hostname: window.location.hostname, app: this});
      $("#main").show().html(res)
      BaseGame.replaceDataSrc();
      resolve();
    });
  }

  // Update specific nunjuck templates marked as updatable twig style with nunjucks
  updateNunjucks() {
    return new Promise((resolve, reject) => {
      const res = this.mainNunjuckTemplate.render({data: this.data, visitorData: this.currentVisitorData, hostname: window.location.hostname, app: this});
      const $res = $(`<div>${res}</div>`);
      $res.find("img").each(function (i, img) {
        if ($(img).data("src")) {
          $(img).attr("src", $(img).data("src"))
        }
      });
      $res.find("*[data-updatable]").each((i, n) => {
        const id = $(n).attr("data-updatable")
        $("#main").find(`[data-updatable='${id}']`).replaceWith(n)
      })
      resolve();
    });
  }

  initScreenSaver() {
    // Timeout (screensaver) related
    this.resetTimer();
    $(document).on("click touchmove touchstart touchend", (e) => {
      this.resetTimer();
    });
    requestInterval(() => this.checkTimer(), 1000);
  }

  initButtons() {

    // Init big buttons dynamic action
    $("#main").on(this.touchEvent, ".help, .trigger, .button, .click-sound", (e) => {
      const el = $(e.currentTarget);

      // If button is disabled, avoid calling the function multiple times
      if (el.is(".disabled")) return;
      // avoid calling for enter key or space
      if (e.type === 'keydown' && !(e.keyCode === 13 || e.keyCode === 32)) return;
      // avoid calling if there's a current action
      if (this.currentAction !== false) return;

      if (typeof el.data("action") != "undefined") {
        const action = el.data("action");

        // Change context when needed
        // Allow us to call subclass functions inside baseGame
        const actionParts = action.split('.');
        let context = this;

        // Recursively navigate down the object tree
        for (let i = 0; i < actionParts.length - 1; i++) {
          const part = actionParts[i];
          if (context && context[part]) {
            context = context[part];
          } else {
            console.error(`BaseGame: Path broken at "${part}". Could not resolve action: ${action}`);
            return;
          }
        }

        const methodName = actionParts[actionParts.length - 1];

        if (typeof context[methodName] == "function") {
          // Action is a function
          const functionArg = (typeof el.data("action-arg") != "undefined" ? el.data("action-arg") : null);
          const functionDelay = (typeof el.data("action-delay") != "undefined" ? el.data("action-delay") : 50);
          console.log("BaseGame: Calling function : " + action + "(" + ((functionArg !== null) ? functionArg : "") + ") in " + functionDelay + " millis");

          // Add active class. If finger is held (not a click) :active is not handled correctly.
          // This help to have visual feedback in both case.
          // Disable button until the event is called
          el.addClass("_active_ disabled");

          requestTimeout((e) => {
            if (functionArg !== null) {
              context[methodName](functionArg, e);
            } else {
              context[methodName](e);
            }
            el.removeClass("_active_ disabled");
            this.currentAction = false;
          }, functionDelay);
        } else {
          console.log(`BaseGame: Action not found for ${action}`);
        }
      }

      // Play audio click
      if (typeof this.settings.click_sound !== "undefined" && this.settings.click_sound != "") {
        this.audio.play(this.settings.click_sound, this.settings.volume.click);
        // console.log("click")
      }

    });

  };

  start() {
    if (!this.ready || this.started) {
      return;
    }

    this.started = true;

    // To be completed in child games
  }

  goToScreen(screenId, callback = null, skipLockScreenCheck = false, options = {}) {
    // Extract transition preferences
    const {
      skipOut = false,
      skipIn = false,
      // In some case, we want to be able to reanimate the same screen
      allowSameScreenTransition = false
    } = options;

    // Stop on lock
    if (!skipLockScreenCheck && this.lockScreen) {
      console.log("prevent locked screen animation")
      return false;
    }

    // Prevent requesting a re-animation of the same screen
    // while being animated to, or currently on a screen.
    if (!allowSameScreenTransition && this.targetScreen === screenId) {
      console.log("prevent same screen animation")
      return;
    }

    this.targetScreen = screenId;

    // Cache the incoming screen element for reuse (step helpers, etc.)
    this.$currentScreen = $(`#screen-${screenId}`);

    // Initialize the incoming screen's steps (first step current, rest
    // hidden). The screen is still hidden here, so this is invisible.
    this.setupScreenSteps();

    this.lockInteraction();

    if (this.screenTimeline) {
      this.screenTimeline.kill();
      this.screenTimeline = null;
    }

    const currentScreen = $(".screen.current");
    const nextScreen = this.$currentScreen;
    this.screenTimeline = gsap.timeline();

    if (currentScreen.length > 1) {
      console.warn("There are multiple screens marked as `current`, which is not a good thing and may create multiple callbacks.")
    }

    // --- Case 1: Transitioning between two screens ---
    if (currentScreen.length > 0 && nextScreen.length > 0) {
      currentScreen.removeClass("current");

      // OUT - Use .set() for instant skip, otherwise .to() for animation
      if (skipOut) {
        this.screenTimeline.set(currentScreen, {alpha: 0});
      } else {
        this.screenTimeline.to(currentScreen, {
          alpha: 0,
          duration: 0.4
        });
      }

      this.screenTimeline.add(() => {
        $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
        $(".screen").addClass("hidden");
        this.initScreen(screenId);
        nextScreen.removeClass("hidden");
        console.log("transition out complete " + this.currentScreen);
      });

      // IN - Use .set() for instant skip, otherwise .to() for animation
      if (skipIn) {
        this.screenTimeline.set(nextScreen, {alpha: 1});
      } else {
        this.screenTimeline.to(nextScreen, {
          alpha: 1,
          duration: 0.4
        });
      }

      this.screenTimeline.add(() => {
        this.currentScreen = screenId;
        this.unlockInteraction();
        // this.targetScreen = null;
        $("#screen-" + screenId).addClass("current");

        console.log("transition in complete " + screenId);
        if (typeof callback == "function") callback();
      });

    }
    // --- Case 2: No current screen (Intro fade) ---
    else if (currentScreen.length === 0 && nextScreen.length > 0) {
      $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
      $(".screen").addClass("hidden");
      this.initScreen(screenId);

      // If there is no current screen and a new screen,fade in new, then callback
      nextScreen.removeClass("hidden");

      if (skipIn) {
        this.screenTimeline.set(nextScreen, {alpha: 1});
      } else {
        this.screenTimeline.to(nextScreen, {
          alpha: 1,
          duration: 0.4
        });
      }

      this.screenTimeline.add(() => {
        this.currentScreen = screenId;
        this.unlockInteraction();
        $("#screen-" + screenId).addClass("current");
        if (typeof callback == "function") callback();
      });

    }
    // --- Case 3: Exit to nothing or Screen 0 ---
    else if (screenId === 0 || (currentScreen.length > 0 && nextScreen.length == 0)) {
      currentScreen.removeClass("current");

      if (skipOut) {
        this.screenTimeline.set(currentScreen, {alpha: 0});
      } else {
        this.screenTimeline.to(currentScreen, {
          alpha: 0,
          duration: 0.4
        });
      }

      this.screenTimeline.add(() => {
        $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
        $(".screen").addClass("hidden");
        this.initScreen(screenId);

        this.currentScreen = screenId;
        this.unlockInteraction();
        // this.targetScreen = null;
        if (typeof callback == "function") callback();
      });
    }

    return true;
  }

  lockInteraction() {
    this.lockScreen = true;
    $("body").css("pointer-events", "none");
  }

  unlockInteraction() {
    this.lockScreen = false;
    $("body").css("pointer-events", "");
  }

  goToNextScreen(callback) {
    // Super constructor
    this.goToScreen(parseInt(this.currentScreen) + 1, callback);
  };

  goToPreviousScreen(callback) {
    // Super constructor
    this.goToScreen(parseInt(this.currentScreen) - 1, callback);
  };

  initScreen(screenId) {
    // To be overriden in child games
  }


  // Prepares steps when arriving on a screen. startStep lets a screen be rebuilt
  // straight onto a later step (like a "try again" skipping an intro step).
  setupScreenSteps(startStep = 1) {
    this.targetStep = null;
    this.currentStep = startStep;

    const steps = this.$currentScreen.find(".step");
    if (steps.length === 0) return;

    gsap.set(steps, {clearProps: "opacity"});
    steps.removeClass("current").addClass("hidden");

    const first = steps.filter(`[data-step="${startStep}"]`);
    first.removeClass("hidden").addClass("current");

    console.log("setupScreenSteps", this.currentStep);
  }


  // Mimics goToScreen, but for steps inside a screen
  goToStep(stepId, callback = null, options = {}) {
    const {
      skipOut = false,
      skipIn = false,
      allowSameStepTransition = false
    } = options;

    // Stop on lock
    if (this.lockScreen) {
      console.log("prevent locked step animation");
      return false;
    }

    // Prevent re-animating the step we're already heading to
    if (!allowSameStepTransition && this.targetStep === stepId) {
      console.log("prevent same step animation");
      return;
    }

    this.targetStep = stepId;
    this.lockInteraction();

    // Fires immediately on step change (current step still visible), before the
    // fade-out. Use this for things that must start at request time;
    // `initStep` runs later, after the fade-out, just before the next fades in.
    this.beforeStep(stepId);

    if (this.stepTimeline) {
      this.stepTimeline.kill();
      this.stepTimeline = null;
    }

    const screen = this.$currentScreen;
    const currentStep = screen.find(".step.current");
    const nextStep = screen.find(`.step[data-step="${stepId}"]`);
    this.stepTimeline = gsap.timeline();

    if (currentStep.length > 1) {
      console.warn("There are multiple steps marked as `current`, which may create multiple callbacks.");
    }

    // --- Case 1: Transitioning between two steps ---
    if (currentStep.length > 0 && nextStep.length > 0) {
      // OUT - keep `.current` so GSAP fades from the resting opacity:1
      if (skipOut) {
        this.stepTimeline.set(currentStep, {alpha: 0});
      } else {
        this.stepTimeline.to(currentStep, {
          alpha: 0,
          duration: 0.4
        });
      }

      this.stepTimeline.add(() => {
        currentStep.removeClass("current");
        screen.find(".step").addClass("hidden");
        this.initStep(stepId);
        nextStep.removeClass("hidden");
      });

      // IN
      if (skipIn) {
        this.stepTimeline.set(nextStep, {alpha: 1});
      } else {
        this.stepTimeline.fromTo(nextStep,
          {
            alpha: 0,
            y: 20
          },
          {
            alpha: 1,
            y: 0,
            duration: 0.4,
            ease: "power1.out"
          });
      }

      this.stepTimeline.add(() => {
        this.currentStep = stepId;
        this.unlockInteraction();
        nextStep.addClass("current");
        this.afterStep(stepId);
        if (typeof callback == "function") callback();
      });
    }
    // --- Case 2: No current step yet (first fade in) ---
    else if (currentStep.length === 0 && nextStep.length > 0) {
      screen.find(".step").addClass("hidden");
      this.initStep(stepId);
      nextStep.removeClass("hidden");

      if (skipIn) {
        this.stepTimeline.set(nextStep, {alpha: 1});
      } else {
        this.stepTimeline.fromTo(nextStep,
          {
            alpha: 0,
            y: 20
          },
          {
            alpha: 1,
            y: 0,
            duration: 0.4,
            ease: "power1.out"
          });
      }

      this.stepTimeline.add(() => {
        this.currentStep = stepId;
        this.unlockInteraction();
        nextStep.addClass("current");
        this.afterStep(stepId);
        if (typeof callback == "function") callback();
      });
    }
    // --- Case 3: Nothing to go to ---
    else {
      this.unlockInteraction();
      this.targetStep = null;
    }

    return true;
  }

  goToNextStep(callback) {
    const current = parseInt(this.$currentScreen.find(".step.current").attr("data-step")) || this.currentStep;
    this.goToStep(current + 1, callback);
  }

  goToPreviousStep(callback) {
    const current = parseInt(this.$currentScreen.find(".step.current").attr("data-step")) || this.currentStep;
    this.goToStep(current - 1, callback);
  }

  // Runs the moment a step change is requested, before the fade-out begins
  // (outgoing step still on screen). For setup that must happen after the
  // fade-out instead, use initStep.
  beforeStep(stepId) {
    // To be overriden in child games
  }

  initStep(stepId) {
    // To be overriden in child games
  }

  // Runs once the step is fully faded in and marked current (interaction is
  // unlocked). Use this for things that must not happen mid-transition, like
  // enabling presses only after the step is completely on screen.
  afterStep(stepId) {
    // To be overriden in child games
  }

  resetTimer() {
    this.lastClick = millis();
  }

  checkTimer(e) {
    const noActionSince = millis() - this.lastClick;
    if (noActionSince > (this.getScreenSaverDelay() * 1000) && this.screensaverDelay != -1) {
      if (this.shouldReset()) {
        this.goToScreenSaver();
      }
    }
  }

  getScreenSaverDelay() {
    return this.screensaverDelay;
  }

  /**
   * Prevent the game from going into screen saver mode
   * This might make sense while watching a video, reading long text, etc
   */
  pauseScreenSaver() {
    this.lockScreenSaver = true;
  }

  /**
   * Allow the game to go into screen saver mode
   */
  unpauseScreenSaver() {
    this.resetTimer();
    this.lockScreenSaver = false;
  }

  goToScreenSaver() {
    // To be overridden in child games
    this.reset(1);
  }

  shouldReset() {
    if (millis() - this.lastReset < 3000) {
      return false;
    }

    this.lastReset = millis();

    // Prevent reset if already reset. The idle screen is always
    // the screen 1 at the step 1.
    // If there is no step on the screen 1, it still works as currentStep
    // defaults to 1
    if (this.currentScreen === 1 && this.currentStep === 1) {
      return false;
    }

    this.lastClick = millis();

    // Prevent reset when screen saver is locked
    if (this.lockScreenSaver === true) {
      return false;
    }

    return true;
  }

  reset(screenId, resetCallback) {
    this.started = false;

    if (typeof screenId == "undefined") {
      screenId = 1;
    }

    // Return to idle screen. Same screen transitions are allowed to allow reset
    // to the same screen step 1 when there are more than 1 steps.
    this.goToScreen(screenId, () => {
      if (typeof resetCallback === "function") resetCallback();
    }, true, {allowSameScreenTransition: true});
  }

  // Preload all audio files from json files
  findAllAudioFiles(objectToParse) {
    return this.findAllExtensionFilesInObject(objectToParse, [".mp3", ".wav", ".oga"]);
  }

  findAllExtensionFilesInObject(objectToParse, extensions) {
    let result = [];

    $.each(objectToParse, (i, element) => {
      if (element instanceof Array || element instanceof Object) {
        const ids = this.findAllExtensionFilesInObject(element, extensions);
        result = result.concat(ids); // or $.merge(result, ids);
      } else if (typeof element == "string") {
        $.each(extensions, (i2, extension) => {
          if (element.endsWith(extension)) {
            result.push(element);
          }
        });
      }

    });

    return result;
  }

  deepMerge(target, source) {
    const result = {...target};

    for (const key of Object.keys(source)) {
      if (
        source[key] !== null &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key]) &&
        key in target
      ) {
        result[key] = this.deepMerge(target[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }

    return result;
  }

  // Sets the real src of images and videos once nunjucks has rendered their data-src
  static replaceDataSrc(selector = "body") {
    $("img, video", selector).each(function (i, el) {
      if ($(el).data("src")) {
        $(el).attr("src", $(el).data("src"))
      }
    })
  }

  restartApp = function () {
    if (window.inElectron()) {
      console.log("Restarting app... calling electronBridge.refreshApp")
      window.electronBridge.refreshApp()
    } else {
      window.location.reload()
    }
  }
}
